import React, { useEffect, useState } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Form as FinalForm, Field } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast } from '../../../util/validators';
import {
  Form,
  FieldSelect,
  InlineTextButton,
  PrimaryButton,
  SecondaryButton,
  Icons,
  NamedLink,
} from '../../../components';
import css from './ProductOrderForm.module.css';
import { addItemToCart, deleteItemFromCart } from '../../../containers/CartPage/CartPage.duck';
import { selectCurrentUser, selectWishlistIds, toggleWishlistItem } from '../../../ducks/user.duck';
import { useDispatch, useSelector } from 'react-redux';
import { doesAcceptOffer, getUserCartDetails } from '../../../util/dataExtractors';
import { isItemAddedToCart } from '../../../containers/CartPage/cartHelpers';
import { selectIsAuthenticated } from '../../../ducks/auth.duck';
import { isArrayLength, showToaster } from '../../../util/genericHelpers';
import { formatVacationUntil } from '../../../util/vacation';
// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;

const RenderForm = formRenderProps => {
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    listing,
    listingId,
    isOwnListing,
    onContactUser,
    values,
    onOpenOffersModal = () => {},
    setIsSocialSharingModalOpen = () => {},
    sellerVacation = null,
    editListingParams = null,
  } = formRenderProps;

  const dispatch = useDispatch();

  // In case quantity is missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity } = values || {};
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else {
      handleSubmit(e);
    }
  };

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  const hasStock = currentStock && currentStock > 0;
  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];
  const hasNoStockLeft = currentStock != null && currentStock === 0;
  const hasOneItemLeft = currentStock != null && currentStock === 1;

  const currentUser = useSelector(selectCurrentUser);
  const cartItems = getUserCartDetails(currentUser);
  const wishlist = useSelector(selectWishlistIds); //HeartIcon
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const showMakeAnOffer = doesAcceptOffer(listing);
  // A guest's cart is in this browser's storage, which the server render can't read: it is read
  // once the form is on the page, so the browser's first render matches the server's.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const isAddedToCart =
    (isAuthenticated || isMounted) &&
    isItemAddedToCart({
      isAuthenticated,
      cartItems,
      listingId: listingId?.uuid,
    });
  const submitDisabled = !hasStock;
  // The cart saves with the busy cursor (CartPage.duck); a click while it saves is ignored.
  // A save that fails leaves the button as it was and says so under it.
  const cartId = listingId?.uuid;
  const cartSaveInProgress = useSelector(
    state =>
      state.CartPage.addItemToCartInProgress.includes(cartId) ||
      state.CartPage.removeItemFromCartInProgress.includes(cartId)
  );
  const cartSaveFailed = useSelector(state => state.CartPage.cartSaveError?.id === cartId);
  const handleCartDetails = () => {
    if (!cartId || cartSaveInProgress) return;
    if (isAddedToCart) {
      dispatch(deleteItemFromCart({ id: cartId }));
    } else {
      dispatch(addItemToCart({ id: cartId, quantity: Number.parseInt(values?.quantity, 10) || 1 }));
    }
  };

  // Saving a favourite shows the busy cursor, not a spinner on the button
  const onHandleFavorites = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!listingId?.uuid) {
      return;
    }
    dispatch(toggleWishlistItem(listingId.uuid)).then(saved => {
      if (!saved) {
        showToaster('error', intl.formatMessage({ id: 'ProductOrderForm.favoriteFailed' }));
      }
    });
  };
  const isFavorited =
    isArrayLength(wishlist) && wishlist.findIndex(e => e === listingId?.uuid) > -1;

  // A seller on vacation (util/vacation.js): what they wrote takes the place
  // of quantity, buy, cart and offer. Questions, favorites and sharing stay.
  const awayUntil = sellerVacation ? formatVacationUntil(intl, sellerVacation) : null;
  const awayNotice = sellerVacation ? (
    <div className={css.awayNotice} role="status">
      <p className={css.awayTitle}>
        {awayUntil ? (
          <FormattedMessage id="ProductOrderForm.sellerAwayUntil" values={{ date: awayUntil }} />
        ) : (
          <FormattedMessage id="ProductOrderForm.sellerAway" />
        )}
      </p>
      {sellerVacation.message ? <p className={css.awayMessage}>{sellerVacation.message}</p> : null}
      <p className={css.awayHelp}>
        <FormattedMessage
          id={
            isOwnListing
              ? 'ProductOrderForm.sellerAwayOwnListing'
              : 'ProductOrderForm.sellerAwayHelp'
          }
        />
      </p>
    </div>
  ) : null;

  return (
    <Form className={css.orderForm} onSubmit={handleFormSubmit}>
      {awayNotice}
      {sellerVacation || hasNoStockLeft || isOwnListing ? null : hasOneItemLeft ? (
        // One left: the quantity is fixed, a hidden input that takes no space
        <Field
          id={`${formId}.quantity`}
          name="quantity"
          component="input"
          type="hidden"
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      ) : (
        <FieldSelect
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label={intl.formatMessage(
            { id: 'ProductOrderForm.quantityLabelAvailable' },
            { available: currentStock }
          )}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption' })}
          </option>
          {quantities.map(quantity => (
            <option key={quantity} value={quantity}>
              {intl.formatMessage({ id: 'ProductOrderForm.quantityOption' }, { quantity })}
            </option>
          ))}
        </FieldSelect>
      )}

      {/* Your own listing: how buyers see it, and the way to edit it */}
      {isOwnListing && !sellerVacation ? (
        <div className={css.ownListingNote} role="status">
          <p className={css.ownListingText}>
            <FormattedMessage id="ProductOrderForm.ownListingNote" />
          </p>
          {editListingParams ? (
            <NamedLink
              className={css.editListingLink}
              name="EditListingPage"
              params={editListingParams}
            >
              <FormattedMessage id="ProductOrderForm.editListing" />
            </NamedLink>
          ) : null}
        </div>
      ) : null}

      {sellerVacation || isOwnListing ? null : (
        <div className={css.orderButtons}>
          <PrimaryButton className={css.buyBtn} type="submit" disabled={submitDisabled}>
            {hasStock ? (
              <FormattedMessage id="ProductOrderForm.ctaButton" />
            ) : (
              <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
            )}
          </PrimaryButton>
          {/* Out of stock: only the greyed button above */}
          {hasStock ? (
            <SecondaryButton className={css.addCartBtn} type="button" onClick={handleCartDetails}>
              {intl.formatMessage({
                id: isAddedToCart
                  ? 'ProductOrderForm.removedFromCart'
                  : 'ProductOrderForm.addedToCart',
              })}
            </SecondaryButton>
          ) : null}
          {hasStock && cartSaveFailed ? (
            <p className={css.error} role="alert">
              <FormattedMessage
                id={
                  isAddedToCart
                    ? 'ProductOrderForm.removeFromCartFailed'
                    : 'ProductOrderForm.addToCartFailed'
                }
              />
            </p>
          ) : null}
          {showMakeAnOffer && hasStock ? (
            <button className={css.offerBtn} type="button" onClick={onOpenOffersModal}>
              <FormattedMessage id="ProductOrderForm.makeAnOffer" />
            </button>
          ) : null}
          <p className={css.finePrint}>
            {hasStock ? (
              <FormattedMessage id="ProductOrderForm.finePrint" />
            ) : showContactUser ? (
              <FormattedMessage
                id="ProductOrderForm.finePrintNoStock"
                values={{ contactSellerLink }}
              />
            ) : null}
          </p>
        </div>
      )}
      {/* Save, ask and share: one bar of real buttons */}
      <div className={css.otherActions}>
        <button
          type="button"
          className={classNames(css.actionButton, css.favorite, {
            [css.favorited]: isFavorited,
          })}
          aria-pressed={isFavorited}
          onClick={e => onHandleFavorites(e)}
        >
          <Icons name="unfavorite" />
          <FormattedMessage id="ProductOrderForm.favorites" />
        </button>
        {isOwnListing ? null : (
          <button type="button" className={css.actionButton} onClick={onContactUser}>
            <Icons name="question" />
            <FormattedMessage id="ProductOrderForm.askQuestion" />
          </button>
        )}
        <button
          type="button"
          className={css.actionButton}
          onClick={() => setIsSocialSharingModalOpen(true)}
        >
          <Icons name="share" />
          <FormattedMessage id="ProductOrderForm.shareText" />
        </button>
      </div>
      {/* <div className={css.paymentMethods}>
        <h2>Payment Methods: </h2>
        <div className={css.paymentMode}>
          <img src={applePay} alt="applePay" />
          <img src={googlePay} alt="google pay" />
          <img src={visa} alt="visa" />
          <img src={mastercard} alt="mastercard" />
          <img src={discover} alt="discover" />
          <img src={amex} alt="amex" />
        </div>
      </div>
      <div className={css.condition}>
        <strong>Returns:</strong> 7 days from receipt. Buyer pays shipping.
      </div>*/}
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { currentStock, deliveryMethod } = props;

  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const initialValues = { ...quantityMaybe, deliveryMethod };

  return <FinalForm initialValues={initialValues} {...props} intl={intl} render={RenderForm} />;
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,

  // other
  onContactUser: func,

  // the seller's vacation (util/vacation.js), when they are away
  sellerVacation: shape({ until: string, message: string }),
};

export default ProductOrderForm;
